import { Location, Place } from '../types';

const isLocation = (item: Location | Place): item is Location => {
  return 'level' in item;
};

export const getBoundsFromPolygon = (coordinates: number[][][]) => {
  if (!coordinates || !coordinates[0]) return null;

  const points = coordinates[0];
  let north = -90;
  let south = 90;
  let east = -180;
  let west = 180;

  points.forEach(([lng, lat]) => {
    north = Math.max(north, lat);
    south = Math.min(south, lat);
    east = Math.max(east, lng);
    west = Math.min(west, lng);
  });

  return { north, south, east, west };
};

export const getPhotoUrl = (photo: string | null) => {
  if (!photo) return '';
  return photo.startsWith('http') ? photo : `${process.env.REACT_APP_API_URL}/uploads/${photo}`;
};

export const flattenLocations = (loc: Location | Place): (Location | Place)[] => {
  if (!loc) return [];
  if (!isLocation(loc)) return [loc];
  const children = loc.children || [];
  return [loc, ...children.flatMap(child => flattenLocations(child))];
};

export const getLocationHierarchy = (location: Location | Place, locationTree: (Location | Place)[]): string => {
  const path: string[] = [location.name];
  let current = location;

  while (true) {
    let parentId: string | undefined;
    
    if (isLocation(current)) {
      const parent = current.parent;
      if (parent) {
        parentId = typeof parent === 'string' ? parent : parent._id;
      }
    } else if (current.neighborhood) {
      parentId = typeof current.neighborhood === 'string' ? current.neighborhood : current.neighborhood._id;
    }

    if (!parentId) break;

    const parent = locationTree.flatMap(loc => flattenLocations(loc)).find(loc => loc._id === parentId);
    if (!parent) break;
    
    path.push(parent.name);
    current = parent;
  }

  return path.join(' / ');
};

// Helper function to get database representation of a Location or Place
export const getDatabaseRepresentation = (loc: Location | Place) => {
  // Create a copy of the location object
  const dbLocation = { ...loc };
  
  if (isLocation(dbLocation)) {
    // Handle Location type
    const { children, ...rest } = dbLocation;
    const result = { ...rest };
    
    if (typeof result.parent === 'object' && result.parent?._id) {
      result.parent = result.parent._id;
    }
    
    return result;
  } else {
    // Handle Place type
    if (typeof dbLocation.neighborhood === 'object' && dbLocation.neighborhood?._id) {
      dbLocation.neighborhood = dbLocation.neighborhood._id;
    }
    
    return dbLocation;
  }
}; 